import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import client, { apiErr } from "../lib/api";
import { PUBLIC_LANGS } from "../i18n";
import { Star, PenLine, MessageSquare, ChevronDown, ChevronUp, Loader2, BadgeCheck } from "lucide-react";

const LANG_LABEL = PUBLIC_LANGS.reduce((m, l) => { m[l.code] = l.native; return m; }, { ENG: "English" });
const MAX = 250;

function Stars({ value, max = 7, size = 15, onPick }) {
  return (
    <div className="inline-flex items-center gap-0.5" role={onPick ? "radiogroup" : undefined}>
      {Array.from({ length: max }, (_, i) => i + 1).map((n) => {
        const filled = n <= Math.round(value || 0);
        const El = onPick ? "button" : "span";
        return (
          <El key={n} type={onPick ? "button" : undefined} onClick={onPick ? () => onPick(n) : undefined}
            data-testid={onPick ? `star-pick-${n}` : undefined}
            className={onPick ? "cursor-pointer transition-transform hover:scale-110" : ""} aria-label={onPick ? `${n} of ${max} stars` : undefined}>
            <Star size={size} className={filled ? "fill-amber-400 text-amber-400" : "text-black/25 dark:text-white/25"} />
          </El>
        );
      })}
    </div>
  );
}

export default function ProgramReview({ programKey, programLabel, defaultLang = "ENG", t: tProp }) {
  const t = tProp || ((k) => k);
  const [data, setData] = useState({ items: [], average: 0, count: 0, langs: [] });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showList, setShowList] = useState(false);
  const [filter, setFilter] = useState("ALL");
  const [f, setF] = useState({ rating: 0, name: "", name_style: "full", email: "", lang: defaultLang, comment: "", comment_en: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const rootRef = useRef(null);

  const openReviews = () => {
    if (!data.count) return;
    setShowList(true);
    setTimeout(() => rootRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 60);
  };

  const load = () => {
    setLoading(true);
    client.get("/public/reviews", { params: { program: programKey } })
      .then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [programKey]);

  const displayName = (raw, style) => {
    const name = (raw || "").trim().replace(/\s+/g, " ");
    if (!name) return "";
    if (style === "first") return name.split(" ")[0];
    if (style === "initials") return name.split(" ").map((p) => p[0]?.toUpperCase()).filter(Boolean).join(".") + ".";
    return name;
  };

  const submit = async () => {
    setErr(""); setMsg("");
    if (!f.rating) return setErr(t("rv_err_rating"));
    if (!f.comment.trim()) return setErr(t("rv_err_comment"));
    setBusy(true);
    try {
      const { data: res } = await client.post("/public/reviews", {
        program: programKey, rating: f.rating, lang: f.lang,
        name: displayName(f.name, f.name_style), email: f.email.trim(),
        comment: f.comment.trim(), comment_en: f.comment_en.trim(),
      });
      setMsg(res.message || t("rv_thanks"));
      setF({ rating: 0, name: "", name_style: "full", email: "", lang: defaultLang, comment: "", comment_en: "" });
      setShowForm(false);
    } catch (e) { setErr(apiErr(e)); }
    setBusy(false);
  };

  const filtered = filter === "ALL" ? data.items : data.items.filter((r) => r.lang === filter);

  return (
    <div ref={rootRef} className="rounded-lg border border-black/10 bg-black/[0.02] p-3 dark:border-white/10 dark:bg-white/[0.03]" data-testid={`reviews-${programKey}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--primary)]" data-testid={`reviews-program-${programKey}`}>{programLabel}</div>
      </div>
      <button type="button" onClick={openReviews} disabled={!data.count}
        className={`mt-1 flex items-center gap-2 rounded-md ${data.count ? "cursor-pointer hover:bg-black/5 dark:hover:bg-white/10" : "cursor-default"} -mx-1 px-1 py-0.5 text-start`}
        data-testid={`reviews-avg-${programKey}`} title={data.count ? "See reviews" : ""}>
        <Stars value={data.average} />
        <span className="text-xs opacity-70">{data.count ? `${data.average}/7 · ${data.count} ${t("rv_reviews_word")}` : t("rv_no_reviews")}</span>
      </button>

      <div className="mt-2 flex flex-wrap gap-2">
        <button type="button" onClick={() => { setShowForm((v) => !v); setMsg(""); setErr(""); }}
          data-testid={`review-write-${programKey}`}
          className="inline-flex items-center gap-1 rounded-md bg-[var(--primary)] px-2.5 py-1 text-xs font-medium text-white hover:bg-[var(--primary-hover)]">
          <PenLine size={13} /> {t("rv_write")}
        </button>
        {data.count > 0 && (
          <button type="button" onClick={() => setShowList((v) => !v)} data-testid={`review-toggle-${programKey}`}
            className="inline-flex items-center gap-1 rounded-md border border-black/15 px-2.5 py-1 text-xs font-medium hover:bg-black/5 dark:border-white/15 dark:hover:bg-white/10">
            <MessageSquare size={13} /> {showList ? t("rv_hide") : t("rv_read")} {showList ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
        )}
        <Link to={`/reviews?program=${encodeURIComponent(programKey)}`} data-testid={`review-see-all-${programKey}`}
          className="inline-flex items-center gap-1 rounded-md border border-black/15 px-2.5 py-1 text-xs font-medium hover:bg-black/5 dark:border-white/15 dark:hover:bg-white/10">
          {t("rv_see_all") || "See all reviews"} →
        </Link>
      </div>

      {msg && <div className="mt-2 rounded-md bg-emerald-500/10 px-2 py-1.5 text-xs text-emerald-700 dark:text-emerald-400" data-testid={`review-thanks-${programKey}`}>{msg}</div>}

      {showForm && (
        <div className="mt-3 space-y-2 border-t border-black/10 pt-3 dark:border-white/10" data-testid={`review-form-${programKey}`}>
          <div className="flex items-center gap-2">
            <span className="text-xs opacity-70">{t("rv_your_rating")}</span>
            <Stars value={f.rating} onPick={(n) => setF({ ...f, rating: n })} />
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder={t("rv_name_ph")}
              data-testid={`review-name-${programKey}`} maxLength={60}
              className="rounded-md border border-black/15 bg-white/70 px-2.5 py-1.5 text-xs dark:border-white/15 dark:bg-black/30" />
            <select value={f.lang} onChange={(e) => setF({ ...f, lang: e.target.value })} data-testid={`review-lang-${programKey}`}
              className="rounded-md border border-black/15 bg-white/70 px-2.5 py-1.5 text-xs dark:border-white/15 dark:bg-black/30">
              {PUBLIC_LANGS.filter((l) => !l.placeholder).map((l) => <option key={l.code} value={l.code}>{l.native}</option>)}
            </select>
          </div>
          {f.name.trim() && (
            <div className="flex flex-wrap items-center gap-2 text-[11px]">
              <span className="opacity-60">{t("rv_show_name_as")}</span>
              <select value={f.name_style} onChange={(e) => setF({ ...f, name_style: e.target.value })} data-testid={`review-name-style-${programKey}`}
                className="rounded-md border border-black/15 bg-white/70 px-2 py-1 dark:border-white/15 dark:bg-black/30">
                <option value="full">{t("rv_name_full")}</option>
                <option value="first">{t("rv_name_first")}</option>
                <option value="initials">{t("rv_name_initials")}</option>
              </select>
              <span className="opacity-70">→ {t("rv_will_display")} <b data-testid={`review-name-preview-${programKey}`}>{displayName(f.name, f.name_style)}</b></span>
            </div>
          )}
          <div>
            <input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} type="email"
              placeholder={t("rv_email_ph")}
              data-testid={`review-email-${programKey}`} maxLength={120}
              className="w-full rounded-md border border-black/15 bg-white/70 px-2.5 py-1.5 text-xs dark:border-white/15 dark:bg-black/30" />
          </div>
          <div>
            <textarea value={f.comment} onChange={(e) => setF({ ...f, comment: e.target.value.slice(0, MAX) })} rows={3}
              placeholder={t("rv_comment_ph")} data-testid={`review-comment-${programKey}`}
              className="w-full rounded-md border border-black/15 bg-white/70 px-2.5 py-1.5 text-xs dark:border-white/15 dark:bg-black/30" />
            <div className="text-end text-[10px] opacity-50">{f.comment.length}/{MAX}</div>
          </div>
          <div className="rounded-md border border-dashed border-black/15 p-2 dark:border-white/15">
            <div className="text-[11px] opacity-75">{t("rv_translate_q")}</div>
            <textarea value={f.comment_en} onChange={(e) => setF({ ...f, comment_en: e.target.value.slice(0, MAX) })} rows={2}
              placeholder={t("rv_comment_en_ph")} data-testid={`review-comment-en-${programKey}`}
              className="mt-1 w-full rounded-md border border-black/15 bg-white/70 px-2.5 py-1.5 text-xs dark:border-white/15 dark:bg-black/30" />
            <div className="text-end text-[10px] opacity-50">{f.comment_en.length}/{MAX}</div>
          </div>
          {err && <div className="text-xs text-[#DA1E28]" data-testid={`review-error-${programKey}`}>{err}</div>}
          <div className="flex justify-end">
            <button type="button" onClick={submit} disabled={busy} data-testid={`review-submit-${programKey}`}
              className="inline-flex items-center gap-1 rounded-md bg-[var(--primary)] px-3 py-1.5 text-xs font-medium text-white hover:bg-[var(--primary-hover)] disabled:opacity-60">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <PenLine size={13} />} {t("rv_submit")}
            </button>
          </div>
          <p className="text-[10px] opacity-50">{t("rv_moderated")}</p>
        </div>
      )}

      {showList && (
        <div className="mt-3 space-y-2 border-t border-black/10 pt-3 dark:border-white/10">
          {data.langs.length > 1 && (
            <label className="flex items-center gap-1.5 text-[11px]">
              <span className="opacity-60">{t("rv_show_in")}</span>
              <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid={`review-filter-${programKey}`}
                className="rounded-md border border-black/15 bg-white/70 px-2 py-1 text-[11px] dark:border-white/15 dark:bg-black/30">
                <option value="ALL">{t("rv_all_langs")}</option>
                {data.langs.map((c) => <option key={c} value={c}>{LANG_LABEL[c] || c}</option>)}
              </select>
            </label>
          )}
          {loading && <div className="text-xs opacity-50">{t("rv_loading")}</div>}
          {!loading && filtered.length === 0 && <div className="text-xs opacity-50">{t("rv_none_lang")}</div>}
          {filtered.map((r) => (
            <div key={r.id} className="rounded-md bg-white/60 p-2 dark:bg-black/20" data-testid={`review-item-${r.id}`}>
              <div className="flex items-center justify-between gap-2">
                <Stars value={r.rating} size={12} />
                <span className="rounded-full bg-black/5 px-1.5 py-0.5 text-[10px] opacity-70 dark:bg-white/10">{LANG_LABEL[r.lang] || r.lang}</span>
              </div>
              <div className="mt-1 text-xs opacity-90">{r.comment}</div>
              {r.comment_en && r.lang !== "ENG" && <div className="mt-1 border-t border-black/5 pt-1 text-[11px] italic opacity-70 dark:border-white/5">EN: {r.comment_en}</div>}
              <div className="mt-1 flex items-center gap-1.5 text-[10px] opacity-50">
                <span>— {r.name || t("rv_anon")}</span>
                {r.verified && <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-500/15 px-1.5 py-0.5 font-medium text-emerald-700 opacity-100 dark:text-emerald-400" data-testid={`review-verified-${r.id}`}><BadgeCheck size={11} /> {t("rv_verified")}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
